import axios from 'axios'
import { request2, baseURL2 } from '@/utils/httpClient'
import { processErrorResponse } from '@/utils/apiErrorMessages'
import i18n from '@/i18n'
import { DEFAULT_LOCALE } from '@/i18n/config'
import { getAuthHeaders } from './requestHelpers'
import { buildCaseDocumentFilename } from '@/views/casecenter/utils/caseDisplay'




const CASE_PDF_ENDPOINTS = {
  quotation: { path: 'quotation', fallback: '見積書.pdf', error: 'Quotation download failed' },
  deliveryNote: { path: 'delivery-note', fallback: '納品書.pdf', error: 'Delivery note download failed' },
  requestForm: { path: 'request-form', fallback: '請求書.pdf', error: 'Request form download failed' },
}

const downloadCasePdf = async (idempotencyKey, stageKey, type, projectName) => {
  const config = CASE_PDF_ENDPOINTS[type] || CASE_PDF_ENDPOINTS.quotation
  const instance = axios.create({ baseURL: baseURL2 })

  let currentLang = localStorage.getItem('lang')
  if (!currentLang) {
    currentLang = JSON.stringify(DEFAULT_LOCALE)
  }
  let language = 'en'
  try {
    language = JSON.parse(currentLang)
  } catch (e) {
    // ignore
  }

  const key = String(idempotencyKey || '').trim()
  const stage = String(stageKey || '').trim()
  const res = await instance({
    url: `/case-info/${encodeURIComponent(key)}/${config.path}/pdf`,
    method: 'GET',
    params: { stage },
    responseType: 'blob',
    headers: getAuthHeaders({ language }),
    validateStatus: () => true,
  })

  const contentType = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || ''

  if (res.status === 401) {
    /** @type {any} */
    const err = new Error('Unauthorized')
    err.status = 401
    throw err
  }

  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    const text = await res.data.text()
    let payload = {}
    try {
      payload = JSON.parse(text)
    } catch (e) {
      payload = { msg: text, status: res.status }
    }
    const processed = processErrorResponse(payload, (keyName) => {
      try {
        return i18n.t(keyName)
      } catch (e2) {
        return null
      }
    })
    /** @type {any} */
    const err = new Error(processed.localizedMsg || processed.msg || config.error)
    err.errorCode = processed.errorCode
    err.payload = processed
    throw err
  }

  if (res.status < 200 || res.status >= 300) {
    /** @type {any} */
    const err = new Error(config.error)
    err.status = res.status
    throw err
  }

  const constructed = buildCaseDocumentFilename(type, stageKey, projectName)
  const disposition = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])
  let filename = constructed || config.fallback
  if (disposition) {
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
    if (utf8Match && utf8Match[1]) {
      try {
        filename = decodeURIComponent(utf8Match[1].trim())
      } catch (e) {
        filename = utf8Match[1].trim()
      }
    } else {
      const plainMatch = /filename="?([^";]+)"?/i.exec(disposition)
      if (plainMatch && plainMatch[1]) {
        filename = plainMatch[1].trim()
      }
    }
  }
  if (!filename || filename === 'document.pdf' || filename === config.fallback) {
    filename = constructed || config.fallback
  }

  return { blob: res.data, filename }
}

// 案件 PDF 下载相关接口
/**
 * Download a case quotation PDF by idempotency key and stage key.
 * @param {string} idempotencyKey
 * @param {string} stageKey
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */

export const downloadCaseQuotation = (idempotencyKey, stageKey, projectName) =>
  downloadCasePdf(idempotencyKey, stageKey, 'quotation', projectName)

export const downloadCaseDeliveryNote = (idempotencyKey, stageKey, projectName) =>
  downloadCasePdf(idempotencyKey, stageKey, 'deliveryNote', projectName)

export const downloadCaseRequestForm = (idempotencyKey, stageKey, projectName) =>
  downloadCasePdf(idempotencyKey, stageKey, 'requestForm', projectName)

// AI 助手对话

export const submitCaseRecord = (formData) => {
  return request2({
    url: '/case-info',
    method: 'POST',
    data: formData,
    headers: getAuthHeaders({
      'Content-Type': 'multipart/form-data',
    }),
  })
}

/** 根据幂等键获取 Case 项目信息 */

export const findAiDesignProjectByIdempotencyKey = (idempotencyKey) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}`,
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

/** 案件に紐づく Make House Village（Office）プロジェクト一覧（未連携なら空配列） */
export const fetchCaseOfficeProjects = (idempotencyKey) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/office-projects`,
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

export const updateCaseCover = (idempotencyKey, file) => {
  const formData = new FormData()
  formData.append('coverFile', file)
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/cover`,
    method: 'PUT',
    data: formData,
    headers: getAuthHeaders(),
  })
}

export const previewCaseCover = (idempotencyKey, options = {}) => {
  const instance = axios.create({ baseURL: baseURL2 })
  const params = {}
  if (options.width) params.w = options.width
  if (options.quality) params.q = options.quality
  if (options.cacheKey) params.v = String(options.cacheKey)
  return instance({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/cover/preview`,
    method: 'GET',
    params,
    responseType: 'blob',
    headers: getAuthHeaders(),
    validateStatus: () => true,
  })
}

/** Case Google Drive 文件列表：GET /case-info/{idempotencyKey}/files */

export const fetchCaseDriveFiles = (idempotencyKey) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/files`,
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

/**
 * Case 协作：MH 担当者目录
 * GET /case-info/doer-list；响应 data.list：{ id, name }（name 优先姓名，否则 username）
 */

export const fetchAiDesignProjectCaseAllList = (params) => {
  const p = params || {}
  return request2({
    url: '/case-info/admin-list',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      pageNum: p.pageNum,
      pageSize: p.pageSize,
      content: p.keyword || p.content || '',
      status: p.status || '',
      priority: p.priority || '',
      createdStart: p.createdStart || '',
      createdEnd: p.createdEnd || '',
      // true のときだけ送る（自分に未読のチャットがある案件だけ）
      unreadOnly: p.unreadOnly ? true : undefined,
    },
  })
}

/** 委托用户等项目分页 */

export const fetchAiDesignProjectCaseList = (params) => {
  const p = params || {}
  return request2({
    url: '/case-info/list',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      pageNum: p.pageNum,
      pageSize: p.pageSize,
      content: p.keyword || p.content || '',
      status: p.status || '',
      priority: p.priority || '',
      createdStart: p.createdStart || '',
      createdEnd: p.createdEnd || '',
      // true のときだけ送る（自分に未読のチャットがある案件だけ）
      unreadOnly: p.unreadOnly ? true : undefined,
    },
  })
}

/** 担当者被分配案件分页 */

export const fetchCaseCollaborationAssigneeCases = (params) => {
  const p = params || {}
  return request2({
    url: '/case-info/assignee-cases',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      pageNum: p.pageNum,
      pageSize: p.pageSize,
      content: p.keyword || p.content || '',
      status: p.status || '',
      priority: p.priority || '',
      createdStart: p.createdStart || '',
      createdEnd: p.createdEnd || '',
      // true のときだけ送る（自分に未読のチャットがある案件だけ）
      unreadOnly: p.unreadOnly ? true : undefined,
    },
  })
}

/** Case 协作：管理员确认检阅无误并开始当前阶段（第 1/2/3 阶段） */

export const confirmCaseCollaborationPhaseOne = (idempotencyKey) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/phase-one/confirm`,
    method: 'POST',
    headers: getAuthHeaders(),
  })
}

/**
 * 案件管理者以上：段階ガントチャートの日程を直す。stages＝[{ stageKey, startDate, dueDate }]（変えた段階だけ）。
 * customRows＝足した日程だけの行 [{ id, name, startDate, dueDate, beforeStage }]（送ったときだけ丸ごと置き換え、null は変更なし）
 */

export const updateCaseStageSchedule = (idempotencyKey, stages, customRows = null) => {
  const key = String(idempotencyKey || '').trim()
  const data = { stages: Array.isArray(stages) ? stages : [] }
  if (Array.isArray(customRows)) data.customRows = customRows
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/stages/schedule`,
    method: 'PUT',
    data,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/** Case 协作：生成当前阶段报价单（案件管理者，或有案件担当的 MH社員以上） */

export const generateCaseQuotation = (idempotencyKey, body) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/quotation/generate`,
    method: 'POST',
    data: body || {},
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/** 見積書の修正：見積確認中（段階の開始前）だけ。金額明細を入れ替え、発行日は修正した日になる */
export const updateCaseQuotation = (idempotencyKey, body) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/quotation`,
    method: 'PUT',
    data: body || {},
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

export const closeCase = (idempotencyKey) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/close`,
    method: 'POST',
    headers: getAuthHeaders(),
  })
}

/** プラットフォーム管理者だけ：案件を削除する（一覧・詳細から消える） */
export const deleteCase = (idempotencyKey) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}`,
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
}

/** プラットフォーム管理者だけ：依頼者のメールアドレス（通知先）を直す */
export const updateCaseContactEmail = (idempotencyKey, email) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/contact-email`,
    method: 'PUT',
    data: { email: String(email || '').trim() },
    headers: getAuthHeaders(),
  })
}

/** プラットフォーム管理者だけ：依頼情報（案件依頼のフォームの内容）を直す。extraInfo は作成時と同じ形 */
export const updateCaseRequestInfo = (idempotencyKey, payload) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/request-info`,
    method: 'PUT',
    data: payload,
    headers: getAuthHeaders(),
  })
}

export const confirmCaseCurrentStageCompleted = (idempotencyKey) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/stages/current/confirm-completed`,
    method: 'POST',
    headers: getAuthHeaders(),
  })
}

export const submitCaseProductRequestFeedback = (idempotencyKey, body) => {
  const key = String(idempotencyKey || '').trim()
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/stages/product-request/feedback`,
    method: 'POST',
    data: body || {},
    headers: getAuthHeaders({
      'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
    }),
  })
}

const appendCaseStageTransitionFiles = (fd, filesByField) => {
  Object.entries(filesByField || {}).forEach(([fieldName, files]) => {
    const list = Array.isArray(files) ? files.filter(Boolean) : []
    list.forEach((file) => {
      fd.append(fieldName, file)
    })
  })
}

const submitCaseStageTransitionForm = (path, idempotencyKey, transitionType, payload, filesByField, fileMeta) => {
  const key = String(idempotencyKey || '').trim()
  const fd = new FormData()
  fd.append('transitionType', String(transitionType || '').trim())
  fd.append('payload', JSON.stringify(payload || {}))
  if (fileMeta && typeof fileMeta === 'object') {
    fd.append('fileMeta', JSON.stringify(fileMeta))
  }
  appendCaseStageTransitionFiles(fd, filesByField)
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/stages/${path}`,
    method: 'POST',
    data: fd,
    headers: getAuthHeaders(),
  })
}

export const saveCaseStageTransitionDraft = (idempotencyKey, transitionType, payload, filesByField, fileMeta) =>
  submitCaseStageTransitionForm('transition-draft', idempotencyKey, transitionType, payload, filesByField, fileMeta)

export const submitCaseStageTransition = (idempotencyKey, transitionType, payload, filesByField, fileMeta) =>
  submitCaseStageTransitionForm('transition-submit', idempotencyKey, transitionType, payload, filesByField, fileMeta)

/** Case 协作：管理员当前阶段子任务全部完成后确认进入商品请求阶段 */

export const advanceCaseCollaborationToNextStage = (idempotencyKey, payload) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/stages/advance`,
    method: 'POST',
    data: payload || {},
    headers: getAuthHeaders(),
  })
}

/** Case 协作：管理员第三阶段子任务全部完成后确认项目完结 */

export const confirmCaseCollaborationProjectComplete = (idempotencyKey) => {
  const key = String(idempotencyKey || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/complete`,
    method: 'POST',
    headers: getAuthHeaders(),
  })
}

/** Case 附件：删除（query：idempotencyKey + objectKey） */

export const deleteCaseAttachment = ({ idempotencyKey, objectKey }) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/attachments`,
    method: 'DELETE',
    headers: getAuthHeaders(),
    params: { idempotencyKey, objectKey },
  })
}

/** Case 成果物：删除（query：idempotencyKey + objectKey） */

export const deleteCaseDeliverable = ({ idempotencyKey, objectKey }) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/deliverables`,
    method: 'DELETE',
    headers: getAuthHeaders(),
    params: { idempotencyKey, objectKey },
  })
}

/** Case 成果物：新建文件夹 */

export const createCaseDeliverableFolder = ({ idempotencyKey, folderName, parentFolderId }) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/deliverables/folders`,
    method: 'POST',
    data: {
      folderName: folderName || '',
      parentFolderId: parentFolderId || '',
    },
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

/** Case 成果物：删除文件夹（query：objectKey） */

export const deleteCaseDeliverableFolder = ({ idempotencyKey, objectKey }) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/deliverables/folders`,
    method: 'DELETE',
    headers: getAuthHeaders(),
    params: { objectKey },
  })
}

/** Case 依頼資料：新建文件夹（body：folderName、parentFolderId 省略时建在依頼資料直下） */

export const createCaseAttachmentFolder = ({ idempotencyKey, folderName, parentFolderId }) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/attachments/folders`,
    method: 'POST',
    data: {
      folderName: folderName || '',
      parentFolderId: parentFolderId || '',
    },
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

/** Case 依頼資料：删除文件夹（query：objectKey） */

export const deleteCaseAttachmentFolder = ({ idempotencyKey, objectKey }) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/attachments/folders`,
    method: 'DELETE',
    headers: getAuthHeaders(),
    params: { objectKey },
  })
}

/**
 * 案件文件单文件流式上传：文件原始字节作为请求体，文件名/类型走 query，
 * onUploadProgress 提供上传进度。
 */

const streamUploadCaseFile = (
  category,
  idempotencyKey,
  file,
  onUploadProgress,
  signal,
  displayName,
  displayIndex,
  parentFolderId
) => {
  const key = String(idempotencyKey || '').trim()
  const params = [
    `fileName=${encodeURIComponent((file && file.name) || 'file.bin')}`,
    `mimeType=${encodeURIComponent((file && file.type) || '')}`,
  ]
  if (displayName) params.push(`displayName=${encodeURIComponent(displayName)}`)
  if (displayIndex != null && Number(displayIndex) > 0) {
    params.push(`displayIndex=${encodeURIComponent(String(displayIndex))}`)
  }
  if (parentFolderId) {
    params.push(`parentFolderId=${encodeURIComponent(String(parentFolderId))}`)
  }
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/${category}/stream-upload?${params.join('&')}`,
    method: 'POST',
    data: file,
    headers: getAuthHeaders({ 'Content-Type': 'application/octet-stream' }),
    onUploadProgress,
    signal,
  })
}

export const streamUploadCaseAttachment = (
  idempotencyKey,
  file,
  onUploadProgress,
  signal,
  displayName,
  displayIndex,
  parentFolderId
) =>
  streamUploadCaseFile(
    'attachments',
    idempotencyKey,
    file,
    onUploadProgress,
    signal,
    displayName,
    displayIndex,
    parentFolderId || ''
  )

export const streamUploadCaseDeliverable = (
  idempotencyKey,
  file,
  onUploadProgress,
  signal,
  displayName,
  displayIndex,
  parentFolderId
) =>
  streamUploadCaseFile(
    'deliverables',
    idempotencyKey,
    file,
    onUploadProgress,
    signal,
    displayName,
    displayIndex,
    parentFolderId
  )

/** Case 文件下载（blob；query：idempotencyKey + objectKey） */

export const downloadCaseFileBlob = ({ idempotencyKey, objectKey, onDownloadProgress }) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/files/download`,
    method: 'GET',
    headers: getAuthHeaders(),
    params: { idempotencyKey, objectKey },
    responseType: 'blob',
    onDownloadProgress,
    validateStatus: () => true,
  })
}

/** フォルダを中身ごと ZIP でダウンロード（成果物・依頼資料） */
export const downloadCaseFolderZipBlob = ({ idempotencyKey, objectKey, onDownloadProgress }) => {
  return request2({
    url: `/case-info/${encodeURIComponent(String(idempotencyKey || '').trim())}/files/download-folder`,
    method: 'GET',
    headers: getAuthHeaders(),
    params: { objectKey },
    responseType: 'blob',
    onDownloadProgress,
    validateStatus: () => true,
  })
}

/**
 * 创建 ai_design_info（multipart：projectId, projectName, algorithm, orderId, priority 及算法专有字段/文件）
 * 后端：POST /ai-design-info
 */

// 案件聊天相关接口

export const aiAssistantChat = (payload) => {
  return request2({
    url: '/api/ai_assistant/chat',
    method: 'POST',
    data: payload,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

export const fetchChatThreads = ({ targetType, targetKey }) => {
  return request2({
    url: '/chat/threads',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      targetType,
      targetKey,
    },
  })
}

/** S-AI のホーム「設計サポートの新着メッセージ」：見える案件のチャットの新しい発言（自分の発言を除く）→ { messages } */
export const fetchChatRecent = (limit = 8) => {
  return request2({
    url: '/chat/recent',
    method: 'GET',
    headers: getAuthHeaders(),
    params: { limit },
  })
}

/** 案件一覧の未読バッジ用：targetKeys（idempotency_key 配列）ごとの未読件数 → { unread: { key: count } } */
export const fetchChatUnreadSummary = ({ targetType, targetKeys }) => {
  return request2({
    url: '/chat/unread-summary',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      targetType,
      targetKeys: (Array.isArray(targetKeys) ? targetKeys : []).filter(Boolean).join(','),
    },
  })
}

/** 既読位置を進める（messageId 省略時は最新まで） */
export const markChatThreadRead = (threadId, messageId) => {
  return request2({
    url: '/chat/threads/read',
    method: 'POST',
    headers: getAuthHeaders(),
    params: {
      threadId,
      messageId: messageId || '',
    },
  })
}

export const fetchChatMessages = (threadId, options = {}) => {
  const params = {
    threadId,
  }
  if (options && Number(options.limit) > 0) {
    params.limit = Number(options.limit)
  }
  if (options && options.beforeMessageId) {
    params.beforeMessageId = options.beforeMessageId
  }
  if (options && options.afterMessageId) {
    params.afterMessageId = options.afterMessageId
  }
  if (options && Array.isArray(options.messageIds) && options.messageIds.length) {
    params.messageIds = options.messageIds
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .join(',')
  }
  // 前回の serverTime：それ以降に編集・削除されたメッセージも返ってくる
  if (options && options.changedSince) {
    params.changedSince = options.changedSince
  }
  return request2({
    url: '/chat/messages',
    method: 'GET',
    headers: getAuthHeaders(),
    params,
  })
}

export const createChatMessage = ({ threadId, content }) => {
  return request2({
    url: '/chat/messages',
    method: 'POST',
    data: {
      threadId,
      content,
    },
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/** 自分のメッセージの本文を直す */
export const editChatMessage = ({ threadId, messageId, content }) => {
  return request2({
    url: '/chat/messages/edit',
    method: 'POST',
    data: {
      threadId,
      messageId,
      content,
    },
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/** 自分のメッセージを消す */
export const deleteChatMessage = ({ threadId, messageId }) => {
  return request2({
    url: '/chat/messages/delete',
    method: 'POST',
    data: {
      threadId,
      messageId,
    },
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

export const uploadChatMessageAttachments = (formData) => {
  return request2({
    url: '/chat/messages',
    method: 'POST',
    data: formData,
    headers: getAuthHeaders({
      'Content-Type': 'multipart/form-data',
    }),
  })
}

export const retryChatMessageAttachments = ({ threadId, messageId, uploadIds }) => {
  return request2({
    url: '/chat/messages/attachments/retry',
    method: 'POST',
    data: {
      threadId,
      messageId,
      uploadIds,
    },
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

export const downloadChatMessageAttachmentBlob = ({ threadId, messageId, objectKey, onDownloadProgress }) => {
  return request2({
    url: '/chat/messages/attachments/download',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      threadId,
      messageId,
      objectKey,
    },
    responseType: 'blob',
    onDownloadProgress,
    validateStatus: () => true,
  })
}

export const fetchLineBindingStatus = () => {
  return request2({
    url: '/line/bindings/me',
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

export const unbindLineAccount = () => {
  return request2({
    url: '/line/bindings/me',
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
}

// 案件管理相关接口

export const fetchCaseCollaborationExecutors = () => {
  return request2({
    url: '/case-info/doer-list',
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

/** Case 协作：活跃委托业者目录；响应 data.list：{ id: contractor_uuid, name } */

export const fetchCaseOutsourceContractors = () => {
  return request2({
    url: '/case-info/outsource-contractors',
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

/** 案件管理者：段階の担当者を丸ごと置き換える PUT JSON /case-info/{idempotencyKey}/stage-assignees { stageKey, userIds } */

export const setCaseStageAssignees = (body) => {
  const key = String((body && body.idempotencyKey) || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/stage-assignees`,
    method: 'PUT',
    data: { stageKey: body.stageKey, userIds: body.userIds },
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/** Case 协作：管理员分配子任务 POST JSON /case-info/{idempotencyKey}/subtasks/assign */

export const assignCaseCollaborationSubtask = (body) => {
  const key = String((body && body.idempotencyKey) || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/subtasks/assign`,
    method: 'POST',
    data: body,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/** Case 协作：担当者完成已分配给自己的子任务  */

export const completeCaseCollaborationSubtask = (body) => {
  const key = String((body && body.idempotencyKey) || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/subtasks/complete`,
    method: 'POST',
    data: body,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/** Case 协作：担当者为已分配给自己的子任务登记外注信息 */

export const recordCaseCollaborationSubtaskOutsource = (body) => {
  const key = String((body && body.idempotencyKey) || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/subtasks/outsource`,
    method: 'POST',
    data: body,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

export const updateCaseCollaborationSubtaskOutsource = (body) => {
  const key = String((body && body.idempotencyKey) || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/subtasks/outsource`,
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

export const approveCaseCollaborationSubtaskOutsource = (body) => {
  const key = String((body && body.idempotencyKey) || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/subtasks/outsource/approve`,
    method: 'POST',
    data: body,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

export const rejectCaseCollaborationSubtaskOutsource = (body) => {
  const key = String((body && body.idempotencyKey) || '').trim()
  return request2({
    url: `/case-info/${encodeURIComponent(key)}/subtasks/outsource/reject`,
    method: 'POST',
    data: body,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/**
 * Case 管理：管理员创建委托用户 / 担当者账号
 * body: { accountType: 'user' | 'doer', username, displayName?, email, password }
 */

export const registerCaseCollaborationAccount = (body) => {
  return request2({
    url: '/case/management/accounts',
    method: 'POST',
    data: body,
    headers: getAuthHeaders({
      'Content-Type': 'application/json',
    }),
  })
}

/**
 * Case 管理：管理员通过 CSV 批量发行账号
 */

export const registerCaseCollaborationAccountsBatch = (formData) => {
  return request2({
    url: '/case/management/accounts/batch',
    method: 'POST',
    data: formData,
    headers: getAuthHeaders({
      'Content-Type': 'multipart/form-data',
    }),
  })
}

export const fetchCaseManagementProducts = () => {
  return request2({
    url: '/case/management/products',
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

export const fetchCaseManagementOutsourceContractors = () => {
  return request2({
    url: '/case/management/outsource-contractors',
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

export const createCaseManagementOutsourceContractor = (body) => {
  return request2({
    url: '/case/management/outsource-contractors',
    method: 'POST',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

export const createCaseManagementOutsourceContractorsBatch = (body) => {
  return request2({
    url: '/case/management/outsource-contractors/batch',
    method: 'POST',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

export const updateCaseManagementOutsourceContractor = (contractorUuid, body) => {
  return request2({
    url: `/case/management/outsource-contractors/${encodeURIComponent(contractorUuid)}`,
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

export const deleteCaseManagementOutsourceContractor = (contractorUuid) => {
  return request2({
    url: `/case/management/outsource-contractors/${encodeURIComponent(contractorUuid)}`,
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
}

// ---- 账号权限管理（CaseAccountAdminController）----

/** 搜索账号（分页）；params: { content?, userType?, preset?, caseRole?, product?, pageNum?, pageSize? } */

export const fetchCaseAccounts = (params) => {
  const p = params || {}
  return request2({
    url: '/case/management/accounts',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      content: p.content || '',
      userType: p.userType || '',
      preset: p.preset || '',
      caseRole: p.caseRole || '',
      capability: p.capability || '',
      product: p.product || '',
      walletStatus: p.walletStatus || '',
      subscriptionStatus: p.subscriptionStatus || '',
      pageNum: p.pageNum || 1,
      pageSize: p.pageSize || 20,
    },
  })
}

/** 更新账号基础资料；body: { name?, companyName?, address? } */

export const updateCaseAccountProfile = (username, body) => {
  return request2({
    url: `/case/management/accounts/${encodeURIComponent(username)}/profile`,
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

/** 授予案件角色 + 商品范围；body: { caseRole: 'SUBMITTER'|'DOER'|'ADMIN', productCodes?: string[] } */

export const grantCaseAccountRole = (username, body) => {
  return request2({
    url: `/case/management/accounts/${encodeURIComponent(username)}/case-role`,
    method: 'POST',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

/** 撤销案件角色（同时清空商品范围） */

export const revokeCaseAccountRole = (username) => {
  return request2({
    url: `/case/management/accounts/${encodeURIComponent(username)}/case-role`,
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
}

/** 单独编辑商品范围；body: { productCodes: string[] }（空表示全部商品） */

export const updateCaseAccountProducts = (username, body) => {
  return request2({
    url: `/case/management/accounts/${encodeURIComponent(username)}/products`,
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

/**
 * 「権限」ダイアログの保存：テンプレート＋追加能力をまとめて更新
 * body: { preset: 'RENDER_USER'|'SUBMITTER'|'MH_MEMBER'|'MH_PARTNER'|'DOER'|'CASE_ADMIN'|'PLATFORM_ADMIN',
 *         productCodes?: string[]（SUBMITTER のみ。['*']=全商品、[]=商品なし）,
 *         renderAccess?: boolean, manageProducts?: boolean, manageRender?: boolean（null=変更しない） }
 */

export const updateCaseAccountPermission = (username, body) => {
  return request2({
    url: `/case/management/accounts/${encodeURIComponent(username)}/permission`,
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

/** 更新账号的日历通知邮箱（交期预警等日历事件的实际收件地址；传空串表示清空） */

export const updateCaseAccountCalendarEmail = (username, body) => {
  return request2({
    url: `/case/management/accounts/${encodeURIComponent(username)}/calendar-notify-email`,
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

/** 授予"能管理别的账号商品范围"能力（仅平台管理员可调） */

/** 統計「AI real CG統計」タブ：ユーザーごとの利用状況（ページング） */
export const fetchAiRealCgUserStats = (params) => {
  const p = params || {}
  return request2({
    url: '/case/management/ai-real-cg-stats',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      keyword: p.keyword || '',
      preset: p.preset || '',
      subscription: p.subscription || '',
      pageNum: p.pageNum || 1,
      pageSize: p.pageSize || 20,
    },
  })
}

/** 統計「AI real CG フィードバック」タブ：結果画像フィードバック一覧（案件管理者） */
export const fetchAiRealCgFeedback = (pageNum, pageSize, content) => {
  return request2({
    url: '/case/management/ai-real-cg-feedback',
    method: 'GET',
    headers: getAuthHeaders(),
    params: { pageNum: pageNum || 1, pageSize: pageSize || 6, content: content || '' },
  })
}

/** 統計「AI real CG 生成履歴」タブ：生成 1 回ごとの履歴（入力画像・条件・結果画像、ページング） */
export const fetchAiRealCgGenerationHistory = (params) => {
  const p = params || {}
  return request2({
    url: '/case/management/ai-real-cg-history',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      keyword: p.keyword || '',
      mode: p.mode || '',
      from: p.from || '',
      to: p.to || '',
      pageNum: p.pageNum || 1,
      pageSize: p.pageSize || 10,
    },
  })
}

/** 統計「AI real CG統計」タブのサマリ（KPI・日別推移・当月/前月比較） */
export const fetchAiRealCgUserStatsSummary = (params) => {
  const p = params || {}
  return request2({
    url: '/case/management/ai-real-cg-stats/summary',
    method: 'GET',
    headers: getAuthHeaders(),
    params: { keyword: p.keyword || '', preset: p.preset || '', subscription: p.subscription || '' },
  })
}

export const fetchCaseManagementExecutorPerformance = (params) => {
  return request2({
    url: '/case/management/executor-performance',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      periodStart: params.periodStart || '',
      periodEnd: params.periodEnd || '',
      assigneeKeyword: params.assigneeKeyword || '',
    },
  })
}

/** 案件管理：管理员案件状态汇总（待分配 / 进行中 / 已完成及分布） */

export const fetchCaseManagementStatusSummary = () => {
  return request2({
    url: '/case/management/status-summary',
    method: 'GET',
    headers: getAuthHeaders(),
  })
}

/**
 * Case 管理：审计日志（管理员分页）
 */

export const fetchCaseManagementAuditLogs = (params) => {
  const p = params || {}
  return request2({
    url: '/case/management/audit-logs',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      pageNum: p.pageNum != null ? p.pageNum : 1,
      pageSize: p.pageSize != null ? p.pageSize : 20,
      createdStart: p.createdStart || '',
      createdEnd: p.createdEnd || '',
      eventCategory: p.eventCategory || '',
      eventType: p.eventType || '',
      eventResult: p.eventResult || '',
      actorKeyword: p.actorKeyword || '',
      keyword: p.keyword || '',
    },
  })
}

/**
 * Case 管理：按当前筛选条件导出审计日志 CSV（管理员）
 */

export const exportCaseManagementAuditLogsCsv = (params) => {
  const p = params || {}
  return request2({
    url: '/case/management/audit-logs/export',
    method: 'GET',
    responseType: 'blob',
    headers: getAuthHeaders(),
    params: {
      createdStart: p.createdStart || '',
      createdEnd: p.createdEnd || '',
      eventCategory: p.eventCategory || '',
      eventType: p.eventType || '',
      eventResult: p.eventResult || '',
      actorKeyword: p.actorKeyword || '',
      keyword: p.keyword || '',
    },
  })
}

/** 管理者项目分页 */

// 商品文件库相关接口

export const fetchProductCatalogFiles = (params = {}) => {
  return request2({
    url: '/product-catalog/files',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      tags: params.tags || '',
      keyword: params.keyword || '',
      fileType: params.fileType || '',
      pageNum: params.pageNum || 1,
      pageSize: params.pageSize || 20,
    },
  })
}

/** 上传商品资料文件；formData: file/title/tags */

export const uploadProductCatalogFile = (formData) => {
  return request2({
    url: '/product-catalog/files',
    method: 'POST',
    data: formData,
    headers: getAuthHeaders({ 'Content-Type': 'multipart/form-data' }),
  })
}

/** 更新商品资料文件；body: { title, tags } */

export const updateProductCatalogFile = (fileUuid, body) => {
  return request2({
    url: `/product-catalog/files/${encodeURIComponent(fileUuid)}`,
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

/** 差替商品资料文件本体（title/tags/fileUuid 不变）；formData: { file } */

export const replaceProductCatalogFile = (fileUuid, formData) => {
  return request2({
    url: `/product-catalog/files/${encodeURIComponent(fileUuid)}/replace`,
    method: 'POST',
    data: formData,
    headers: getAuthHeaders({ 'Content-Type': 'multipart/form-data' }),
  })
}

export const deleteProductCatalogFile = (fileUuid) => {
  return request2({
    url: `/product-catalog/files/${encodeURIComponent(fileUuid)}`,
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
}

export const recordProductCatalogFileDetailOpen = (fileUuid) => {
  return request2({
    url: `/product-catalog/files/${encodeURIComponent(fileUuid)}/detail-open`,
    method: 'POST',
    headers: getAuthHeaders(),
  })
}

export const previewProductCatalogFile = (fileUuid, options = {}) => {
  const instance = axios.create({ baseURL: baseURL2 })
  const params = {}
  if (options.width) params.w = options.width
  if (options.quality) params.q = options.quality
  if (options.thumbnail) params.thumbnail = true
  // 差替后避免命中浏览器缓存（预览接口 Cache-Control 为 1 天）
  if (options.version) params.v = String(options.version)
  return instance({
    url: `/product-catalog/files/${encodeURIComponent(fileUuid)}/preview`,
    method: 'GET',
    params,
    responseType: 'blob',
    headers: getAuthHeaders(),
    validateStatus: () => true,
  })
}

/**
 * ZIP の中身一覧。書庫そのものは落とさず、サーバが末尾の目次だけを読んで返す。
 * 数百 MB の図面 ZIP でも一覧が出る（ブラウザで開くと丸ごと落とす羽目になる）。
 */
export const fetchProductCatalogZipEntries = (fileUuid, limit) => {
  return request2({
    url: `/product-catalog/files/${encodeURIComponent(fileUuid)}/zip-entries`,
    method: 'GET',
    headers: getAuthHeaders(),
    params: limit ? { limit } : {},
  })
}

export const downloadProductCatalogFile = (fileUuid) => {
  const instance = axios.create({ baseURL: baseURL2 })
  return instance({
    url: `/product-catalog/files/${encodeURIComponent(fileUuid)}/download`,
    method: 'GET',
    responseType: 'blob',
    headers: getAuthHeaders(),
    validateStatus: () => true,
  })
}

/** 選択した複数ファイルを ZIP でまとめてダウンロード（最大 50 件） */
export const downloadProductCatalogFilesZip = (fileUuids) => {
  const instance = axios.create({ baseURL: baseURL2 })
  return instance({
    url: '/product-catalog/files/download-zip',
    method: 'POST',
    data: { fileUuids: Array.isArray(fileUuids) ? fileUuids : [] },
    responseType: 'blob',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    validateStatus: () => true,
  })
}

export const fetchProductCatalogTags = (params = {}) => {
  return request2({
    url: '/product-catalog/tags',
    method: 'GET',
    headers: getAuthHeaders(),
    params,
  })
}

export const fetchProductCatalogTagViewCounts = (params = {}) => {
  return request2({
    url: '/product-catalog/tags/view-counts',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      createdStart: params.createdStart || '',
      createdEnd: params.createdEnd || '',
    },
  })
}

export const createProductCatalogTag = (body) => {
  return request2({
    url: '/product-catalog/tags',
    method: 'POST',
    data: body,
    headers: getAuthHeaders(),
  })
}

export const updateProductCatalogTag = (tagUuid, body) => {
  return request2({
    url: `/product-catalog/tags/${encodeURIComponent(tagUuid)}`,
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

export const updateProductCatalogTagOrder = (body) => {
  return request2({
    url: '/product-catalog/tags/order',
    method: 'PUT',
    data: body,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  })
}

export const updateProductCatalogTagCover = (tagUuid, file) => {
  const formData = new FormData()
  formData.append('coverFile', file)
  return request2({
    url: `/product-catalog/tags/${encodeURIComponent(tagUuid)}/cover`,
    method: 'PUT',
    data: formData,
    headers: getAuthHeaders(),
  })
}

export const previewProductCatalogTagCover = (tagUuid, options = {}) => {
  const instance = axios.create({ baseURL: baseURL2 })
  const params = {}
  if (options.width) params.w = options.width
  if (options.quality) params.q = options.quality
  if (options.cacheKey) params.v = String(options.cacheKey)
  return instance({
    url: `/product-catalog/tags/${encodeURIComponent(tagUuid)}/cover/preview`,
    method: 'GET',
    params,
    responseType: 'blob',
    headers: getAuthHeaders(),
    validateStatus: () => true,
  })
}

export const deleteProductCatalogTag = (tagUuid) => {
  return request2({
    url: `/product-catalog/tags/${encodeURIComponent(tagUuid)}`,
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
}

export const restoreProductCatalogTag = (tagUuid) => {
  return request2({
    url: `/product-catalog/tags/${encodeURIComponent(tagUuid)}/restore`,
    method: 'PUT',
    headers: getAuthHeaders(),
  })
}

// ==== 管理画面「設定」タブ（保存先フォルダ / メール返信先） ====

export const fetchCaseManagementSettings = () => request2({
  url: '/case/management/settings',
  method: 'GET',
  headers: getAuthHeaders(),
})

/**
 * 全アカウントの権限を、そのままの形で書き出す。一括書き換えの前に取っておく。
 */
export const exportPermissions = () => request2({
  url: '/case/management/permission-export',
  method: 'GET',
  headers: getAuthHeaders(),
})

/**
 * 書き出した JSON を書き戻す。突き合わせは username。
 */
export const restorePermissions = (payload) => request2({
  url: '/case/management/permission-restore',
  method: 'POST',
  headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  data: payload,
})

/**
 * 権限データの正規化。apply=false は試算（1行も書かない）、true で実際に書き込む。
 */
export const normalizePermissions = (apply = false) => request2({
  url: '/case/management/permission-normalize',
  method: 'POST',
  headers: getAuthHeaders(),
  params: { apply },
})

/** OpenRouter のモデル一覧（設定画面の候補）。refresh=true で取り直す */
export const fetchOpenRouterModels = (refresh = false) => request2({
  url: '/case/management/openrouter-models',
  method: 'GET',
  headers: getAuthHeaders(),
  params: { refresh },
})

export const saveCaseManagementSettings = (payload) => request2({
  url: '/case/management/settings',
  method: 'POST',
  headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  data: payload,
})
